/**
 * Pure visibility predicate for the Ask-Praxis FAB (Issue #1713).
 *
 * Extracted from AnalyticsAskFab.jsx so it can be unit-tested without
 * jsdom/mocks — this workspace's vitest runs node-env (no DOM), and the FAB
 * component itself pulls in AuthContext/BrandingContext/AnalyticsAsk.jsx
 * (which touches `document` via the i18n chain), so a bare import would need
 * the same heavy mocking as AnalyticsAskFab.test.jsx just to exercise a
 * boolean. This module has no imports at all — pure boolean predicates.
 *
 * #1713 + follow-up: the facilitator-only FAB does not belong anywhere in the
 * per-run chat/eval flow — it competes with the session UI during the live
 * chat AND clutters the debrief/eval stage (where it stacks above the messenger
 * FAB, #1696, and confuses facilitators reviewing a run). So it hides for ANY
 * chat phase — `OVERLAY` (pre-chat briefing/mode-select), `ACTIVE` (live chat)
 * and `COMPLETE` (debrief/completed run) — using the coarse `<html
 * data-chat-phase>` signal ChatPage publishes for the global HomeButton (#1555,
 * see lib/navigation/chatPhase.js). It is visible only OFF the chat surface,
 * i.e. when no chat phase is present (chatPhase null) — the dashboard/insights
 * context the analytics-ask feature is actually for. (The original #1713 hid
 * only ACTIVE and deliberately kept COMPLETE visible "nothing left to compete
 * with"; the messenger-FAB stack made that debrief-stage FAB a real distraction,
 * so the scope now covers the whole flow.)
 *
 * #2079: also gated by `mobileAllowed`, a boolean the caller precomputes from
 * the shared lib/fabMobileVisibility.js allowlist (mirrors how `isPublic` is
 * precomputed from lib/publicRoutes.js and passed in below) — on mobile, far
 * outside `/dashboard`/`/org-dashboard`, this FAB hides regardless of the
 * chat-phase/role/flag checks above. Kept as a passed-in boolean (not an
 * import here) so this module stays the zero-import pure-predicate pattern
 * its docstring intro describes; the allowlist itself has exactly one
 * definition, in fabMobileVisibility.js, consumed by both FAB entry points.
 */

/**
 * Roles that can use the analytics-ask feature (FAB + /insights + any other
 * entry point). Single source of truth — see `isAnalyticsAskEligible` below.
 */
export const FACILITATOR_ROLES = ['facilitator', 'org_admin', 'admin'];

/**
 * Shared role+flag eligibility gate for the analytics-ask feature (Task 13,
 * self-serve-analytics epic). Extracted so every entry point — the FAB
 * (via `isAskPraxisFabVisible` below) and the /org-dashboard "Insights" nav
 * entry (`OrgDashboardInsightsNav.jsx`) — reads the exact same two conditions
 * and can never drift out of sync.
 *
 * @param {object} user
 * @param {object} [user.org_settings]
 * @param {boolean} [user.org_settings.analytics_ask_enabled]
 * @param {string} [user.role]
 * @returns {boolean}
 */
export function isAnalyticsAskEligible(user) {
  return Boolean(
    user?.org_settings?.analytics_ask_enabled === true
    && FACILITATOR_ROLES.includes(user?.role)
  );
}

/**
 * @param {object} ctx
 * @param {boolean} ctx.isAuthenticated
 * @param {boolean} ctx.roleOk            facilitator/org_admin/admin
 * @param {boolean} ctx.enabled           org_settings.analytics_ask_enabled
 * @param {boolean} ctx.onInsightsPage    /insights hosts the Ask surface inline
 * @param {boolean} ctx.isPublic          isPublicRoute(pathname)
 * @param {(string|null)} ctx.chatPhase   CHAT_PHASE_ATTR value read off <html>
 * @param {boolean} [ctx.mobileAllowed]   isFabAllowedOnMobile(...) (#2079) —
 *                                        defaults to true so callers that
 *                                        don't pass it (desktop-only contexts,
 *                                        older pinned tests) keep prior
 *                                        behaviour unchanged.
 * @returns {boolean}
 */
export function isAskPraxisFabVisible({
  isAuthenticated,
  roleOk,
  enabled,
  onInsightsPage,
  isPublic,
  chatPhase,
  mobileAllowed = true,
} = {}) {
  return Boolean(
    isAuthenticated
    && roleOk
    && enabled
    && !onInsightsPage
    && !isPublic
    && !chatPhase
    && mobileAllowed
  );
}
